import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Check, Sparkles, Palette, Crown, ArrowLeft } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    icon: Palette,
    features: [
      '10 AI art (lifetime)',
      'Try it with friends',
      'Unlimited circles',
      'Unlimited members',
      'Unlimited chat & drawing',
    ],
    cta: 'Current Plan',
    highlight: false,
  },
  {
    id: 'family',
    name: 'Family',
    price: '$9.99',
    period: '/month',
    icon: Sparkles,
    features: [
      '200 AI art/month',
      'Invite whoever you want',
      'Unlimited circles',
      'Unlimited members',
      'Gallery & print ordering',
    ],
    cta: 'Start Family Plan',
    highlight: false,
  },
  {
    id: 'family_plus',
    name: 'Family Plus',
    price: '$19.99',
    period: '/month',
    icon: Crown,
    badge: 'Most Popular',
    features: [
      '400 AI art/month',
      'Invite whoever you want',
      'Unlimited circles',
      'Unlimited members',
      '10% off all prints',
    ],
    cta: 'Go Plus',
    highlight: true,
  },
  {
    id: 'family_pro',
    name: 'Family Pro',
    price: '$34.99',
    period: '/month',
    icon: Crown,
    features: [
      '1,000 AI art/month',
      'Invite whoever you want',
      'Unlimited circles',
      'Unlimited members',
      '20% off all prints',
    ],
    cta: 'Go Pro',
    highlight: false,
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { planId, startCheckout, loading: subLoading } = useSubscription();
  const { currentUser } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const handleUpgrade = async (targetPlan) => {
    if (!currentUser || currentUser.isAnonymous) {
      alert('Please sign in with Google or Apple to subscribe.');
      return;
    }
    setCheckoutLoading(targetPlan);
    try {
      await startCheckout(targetPlan);
    } catch (err) {
      console.error('Checkout failed:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <Container>
      <BackButton onClick={() => navigate(-1)}>
        <ArrowLeft size={20} />
        Back
      </BackButton>
      <Header>
        <Title>Choose Your Plan</Title>
        <Subtitle>Create art together with your family and friends</Subtitle>
      </Header>

      <PlanGrid>
        {PLANS.map((plan, i) => {
          const Icon = plan.icon;
          const isCurrent = planId === plan.id;
          const isUpgrade = !isCurrent && plan.id !== 'free';
          const isDowngrade = planId !== 'free' && plan.id === 'free';

          return (
            <PlanCard
              key={plan.id}
              $highlight={plan.highlight}
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              {plan.badge && <Badge>{plan.badge}</Badge>}
              <PlanIcon $highlight={plan.highlight}>
                <Icon size={24} />
              </PlanIcon>
              <PlanName>{plan.name}</PlanName>
              <PriceRow>
                <Price>{plan.price}</Price>
                <Period>{plan.period}</Period>
              </PriceRow>
              <FeatureList>
                {plan.features.map((f) => (
                  <Feature key={f}>
                    <Check size={16} />
                    <span>{f}</span>
                  </Feature>
                ))}
              </FeatureList>
              {isCurrent ? (
                <CurrentBadge>Current Plan</CurrentBadge>
              ) : isUpgrade ? (
                <UpgradeButton
                  $highlight={plan.highlight}
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={checkoutLoading === plan.id || subLoading}
                >
                  {checkoutLoading === plan.id ? 'Redirecting...' : plan.cta}
                </UpgradeButton>
              ) : isDowngrade ? (
                <DowngradeNote>Manage in account settings</DowngradeNote>
              ) : null}
            </PlanCard>
          );
        })}
      </PlanGrid>

      <Footer>
        <FooterText>All plans include unlimited chat with Ross and drawing on existing art.</FooterText>
        <FooterText>Cancel anytime. No contracts.</FooterText>
      </Footer>
    </Container>
  );
}

const Container = styled.div`
  padding: 24px 16px;
  max-width: 600px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.accent};
  padding: 6px 0;
  margin-bottom: 8px;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 800;
  margin-bottom: 6px;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PlanGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PlanCard = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.radius.large};
  padding: 24px 20px;
  border: 2px solid ${({ $highlight, theme }) => ($highlight ? theme.colors.accent : theme.colors.border)};
  background: ${({ $highlight }) => ($highlight ? 'linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%)' : '#fff')};
`;

const Badge = styled.div`
  position: absolute;
  top: -10px;
  right: 16px;
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: ${({ theme }) => theme.radius.full};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PlanIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.radius.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  background: ${({ $highlight, theme }) => ($highlight ? theme.colors.accent : theme.colors.surface)};
  color: ${({ $highlight, theme }) => ($highlight ? 'white' : theme.colors.textPrimary)};
`;

const PlanName = styled.h3`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 16px;
`;

const Price = styled.span`
  font-size: 28px;
  font-weight: 800;
`;

const Period = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};

  svg {
    color: ${({ theme }) => theme.colors.success};
    flex-shrink: 0;
  }
`;

const UpgradeButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.medium};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $highlight, theme }) => ($highlight ? theme.colors.accent : theme.colors.textPrimary)};
  color: white;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const CurrentBadge = styled.div`
  width: 100%;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.medium};
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DowngradeNote = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textTertiary};
  text-align: center;
  padding: 8px 0;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const FooterText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textTertiary};
  margin-bottom: 4px;
`;
